/**
 * @deprecated This module is deprecated. Use '@services/tracking' instead.
 *
 * Migration guide:
 * - trackEvent, track, trackLogin, setUID → import from '@services/tracking'
 * - Event types (MixPanelEvent, EventName) → import from '@services/tracking'
 * - usePublishedProducts → import from '@services/tracking'
 *
 * This file re-exports from the new tracking module for backward compatibility.
 */

import { Async } from '../../helpers/types';
import { GetAllProgramsQuery, useGetAllProgramsQuery } from 'generated/graphql';
import { useAsyncQuery } from '@client/new/helpers/gql';
import { useUser } from '@client/new/hooks/use-user';

// Re-export from new tracking module
export { trackEvent } from '../tracking';

// Re-export event types from events.ts for backward compatibility
export * from './events';

// ============================================================================
// usePublishedProducts - kept here as it's not related to tracking
// Consider moving to a separate hook file in the future
// ============================================================================

type Product = {
  programId: number;
  collectionType: 'category' | 'program';
  name: string;
  productType: 'otp' | 'subscription';
};

type PublishedProducts = {
  publishedProductType: Product['productType'] | 'both' | 'none';
  publishedCollectionType: Product['collectionType'] | 'both' | 'none';
  products: Product[];
};

export const toPublishedPrograms = (
  v: Pick<NonNullable<GetAllProgramsQuery['creator']>, 'programs'> | null,
): PublishedProducts => {
  if (!v)
    return {
      products: [],
      publishedProductType: 'none',
      publishedCollectionType: 'none',
    };
  const products = (v.programs || [])
    .filter((p) => p.publishedAt && !p.archivedAt)
    .map(
      (p): Product => ({
        programId: p.id,
        collectionType: p.type === 3 ? 'category' : 'program',
        name: p.name || '',
        productType: p.accessType === 'exclusive' ? 'otp' : 'subscription',
      }),
    );

  const otps = products.filter((p) => p.productType === 'otp');
  const subs = products.filter((p) => p.productType === 'subscription');
  const publishedProductType =
    otps.length > 0 && subs.length > 0
      ? 'both'
      : otps.length > 0
      ? 'otp'
      : subs.length > 0
      ? 'subscription'
      : 'none';

  const categories = products.filter((p) => p.collectionType === 'category');
  const programs = products.filter((p) => p.collectionType === 'program');
  const publishedCollectionType =
    categories.length > 0 && programs.length > 0
      ? 'both'
      : categories.length > 0
      ? 'category'
      : programs.length > 0
      ? 'program'
      : 'none';

  return { products: products, publishedProductType, publishedCollectionType };
};

export const usePublishedProducts = (p: {
  influencerId?: string;
}): Async<PublishedProducts> => {
  const user = useUser();
  const query = useGetAllProgramsQuery({
    variables: {
      id: parseInt(`${p.influencerId || user?.influencer?.id || 0}`, 10),
    },
    fetchPolicy: 'network-only',
  });
  const res = useAsyncQuery(query, 'creator', toPublishedPrograms);
  return res.data;
};
